import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { CompactTablePagination, EmptyState, ErrorState, LoadingState, NotFoundPage, PageContainer, SelectionBanner, TablePageShell } from '@/shared/ui';

import {
  useCoupon,
  useCouponCodes,
  useDeactivateCouponCode,
  useExportCouponCodes,
  useGenerateCouponCodes,
  usePauseCoupon,
} from '../../hooks';
import { formatNumber } from '../../lib/format';
import { usePromoPermissions } from '../../lib/usePromoPermissions';
import { useToast } from '../../lib/useToast';
import { COUPON_CODE_STATE_COLOR, COUPON_STATUS_COLOR } from '../../model/statuses';
import type { CouponCodeState } from '../../model/types';

function triggerCsvDownload(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** "Coupon" detail — codes manager (plan-011 §8.5). */
export function CouponDetailPage() {
  const { t } = useTranslation('promotions');
  const { couponId } = useParams<{ couponId: string }>();
  const { canManage } = usePromoPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<CouponCodeState | ''>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateCount, setGenerateCount] = useState('100');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [deactivateConfirm, setDeactivateConfirm] = useState<'whole' | 'selection' | null>(null);

  const couponQuery = useCoupon(couponId);
  const codesQuery = useCouponCodes(couponId);
  const generateMutation = useGenerateCouponCodes();
  const exportMutation = useExportCouponCodes();
  const deactivateCodeMutation = useDeactivateCouponCode();
  const pauseMutation = usePauseCoupon();

  const filteredCodes = useMemo(() => {
    const all = codesQuery.data?.content ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (q && !c.code.toLowerCase().includes(q)) return false;
      if (stateFilter && c.state !== stateFilter) return false;
      return true;
    });
  }, [codesQuery.data, search, stateFilter]);

  const pageItems = filteredCodes.slice(page * size, page * size + size);

  if (couponQuery.isLoading) {
    return (
      <PageContainer>
        <LoadingState label={t('couponDetail.loading')} fullHeight />
      </PageContainer>
    );
  }
  if (couponQuery.isError) {
    if (couponQuery.error?.status === 404) return <NotFoundPage />;
    return (
      <PageContainer>
        <ErrorState
          title={t('couponDetail.loadError.title')}
          message={couponQuery.error?.message ?? t('common:error.unknown')}
          onRetry={() => void couponQuery.refetch()}
        />
      </PageContainer>
    );
  }

  const coupon = couponQuery.data;
  if (!coupon) return <NotFoundPage />;

  const handleGenerate = async () => {
    try {
      const result = await generateMutation.mutateAsync({
        couponId: coupon.id,
        body: { count: Number(generateCount) || 1 },
      });
      success(t('couponDetail.codesGenerated', { count: result.content.length }));
      setGenerateOpen(false);
    } catch (err) {
      notifyError((err as { message?: string }).message ?? t('couponDetail.generateError'));
    }
  };

  const handleExport = async () => {
    try {
      const csv = await exportMutation.mutateAsync(coupon.id);
      triggerCsvDownload(csv, `${coupon.name || coupon.id}-codes.csv`);
    } catch (err) {
      notifyError((err as { message?: string }).message ?? t('couponDetail.exportError'));
    }
  };

  const handleBulkDeactivate = async () => {
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((codeId) => deactivateCodeMutation.mutateAsync({ couponId: coupon.id, codeId })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) success(t('couponDetail.codesDeactivated', { count: ids.length }));
    else notifyError(t('couponDetail.someCodesFailedDeactivate', { failed, total: ids.length }));
    setSelected(new Set());
    setDeactivateConfirm(null);
  };

  return (
    <PageContainer>
      <Button
        component={RouterLink}
        to="/promo/coupons"
        startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
        size="small"
        color="inherit"
        sx={{ alignSelf: 'flex-start', color: 'text.secondary', mb: 2 }}
      >
        {t('couponDetail.backLink')}
      </Button>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {coupon.name || t('builder.unnamedCoupon')}
        </Typography>
        <Chip size="small" variant="outlined" label={t(`couponFormDrawer.kind.${coupon.kind}`)} />
        <Chip size="small" color={COUPON_STATUS_COLOR[coupon.status]} label={coupon.status} />
        <Box sx={{ flex: 1 }} />
        {canManage ? (
          <Stack direction="row" spacing={1}>
            {coupon.kind === 'MULTI_CODE' ? (
              <Button variant="outlined" size="small" startIcon={<QrCode2RoundedIcon />} onClick={() => setGenerateOpen(true)}>
                {t('couponDetail.generateCodes')}
              </Button>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              startIcon={exportMutation.isPending ? <CircularProgress size={16} /> : <DownloadRoundedIcon />}
              disabled={exportMutation.isPending}
              onClick={() => void handleExport()}
            >
              {t('couponDetail.exportCsv')}
            </Button>
            <IconButton size="small" aria-label={t('couponDetail.moreActionsAria')} onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertRoundedIcon />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              {coupon.status !== 'ENDED' ? (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setDeactivateConfirm('whole');
                  }}
                >
                  {t('couponDetail.deactivateCoupon')}
                </MenuItem>
              ) : null}
            </Menu>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadRoundedIcon />}
            disabled={exportMutation.isPending}
            onClick={() => void handleExport()}
          >
            {t('couponDetail.exportCsv')}
          </Button>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('couponDetail.limitsLine', {
          total: coupon.maxRedemptionsTotal != null ? formatNumber(coupon.maxRedemptionsTotal) : '∞',
          perCustomer: coupon.maxRedemptionsPerCustomer != null ? formatNumber(coupon.maxRedemptionsPerCustomer) : '∞',
        })}
      </Typography>

      {codesQuery.isLoading ? (
        <LoadingState label={t('couponDetail.codesLoading')} />
      ) : codesQuery.isError ? (
        <ErrorState
          title={t('couponDetail.codesLoadError')}
          message={codesQuery.error?.message ?? t('common:error.unknown')}
          onRetry={() => void codesQuery.refetch()}
        />
      ) : (codesQuery.data?.content.length ?? 0) === 0 ? (
        <EmptyState
          title={t('couponDetail.noCodes.title')}
          description={t('couponDetail.noCodes.description')}
          action={
            canManage && coupon.kind === 'MULTI_CODE' ? (
              <Button variant="contained" onClick={() => setGenerateOpen(true)}>
                {t('couponDetail.generateCodes')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              placeholder={t('couponDetail.searchCodePlaceholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              select
              size="small"
              label={t('couponDetail.stateFilterLabel')}
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value as CouponCodeState | '');
                setPage(0);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('promotionsList.allStatuses')}</MenuItem>
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="DEACTIVATED">DEACTIVATED</MenuItem>
              <MenuItem value="EXHAUSTED">EXHAUSTED</MenuItem>
            </TextField>
          </Stack>

          {selected.size > 0 ? (
            <SelectionBanner
              label={t('couponDetail.selectedCount', { count: selected.size })}
              onClear={() => setSelected(new Set())}
              actions={
                canManage ? (
                  <Button size="small" color="error" onClick={() => setDeactivateConfirm('selection')}>
                    {t('couponDetail.deactivateSelected')}
                  </Button>
                ) : undefined
              }
            />
          ) : null}

          {filteredCodes.length === 0 ? (
            <EmptyState title={t('couponDetail.codeNotFound')} />
          ) : (
            <TablePageShell
              footer={
                <CompactTablePagination
                  count={filteredCodes.length}
                  page={page}
                  rowsPerPage={size}
                  onPageChange={(_e, next) => setPage(next)}
                  onRowsPerPageChange={(e) => {
                    setSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common:pagination.of')} ${count}`}
                />
              }
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {canManage ? <TableCell padding="checkbox" /> : null}
                      <TableCell>{t('couponDetail.table.code')}</TableCell>
                      <TableCell>{t('couponDetail.table.state')}</TableCell>
                      <TableCell align="right">{t('couponDetail.table.redemptions')}</TableCell>
                      <TableCell align="right">{t('promotionsList.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageItems.map((code) => (
                      <TableRow key={code.id} hover>
                        {canManage ? (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.has(code.id)}
                              onChange={(e) =>
                                setSelected((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(code.id);
                                  else next.delete(code.id);
                                  return next;
                                })
                              }
                            />
                          </TableCell>
                        ) : null}
                        <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{code.code}</TableCell>
                        <TableCell>
                          <Chip size="small" color={COUPON_CODE_STATE_COLOR[code.state]} label={code.state} />
                        </TableCell>
                        <TableCell align="right">{formatNumber(code.redemptionCount)}</TableCell>
                        <TableCell align="right">
                          {canManage && code.state === 'ACTIVE' ? (
                            <Button
                              size="small"
                              color="error"
                              onClick={() =>
                                deactivateCodeMutation.mutate(
                                  { couponId: coupon.id, codeId: code.id },
                                  {
                                    onSuccess: () => success(t('couponDetail.codeDeactivated')),
                                    onError: (e) => notifyError(e.message),
                                  },
                                )
                              }
                            >
                              {t('couponDetail.deactivate')}
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TablePageShell>
          )}
        </>
      )}

      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)}>
        <DialogTitle>{t('couponDetail.generateDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t('couponDetail.generateDialog.hint')}</DialogContentText>
          <TextField
            label={t('couponDetail.generateDialog.countLabel')}
            type="number"
            value={generateCount}
            onChange={(e) => setGenerateCount(e.target.value)}
            slotProps={{ htmlInput: { min: 1, max: 10000 } }}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generateMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={generateMutation.isPending || !generateCount.trim()}
            startIcon={generateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => void handleGenerate()}
          >
            {t('couponDetail.generateDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deactivateConfirm === 'whole'} onClose={() => setDeactivateConfirm(null)}>
        <DialogTitle>{t('couponDetail.deactivateCouponDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('couponDetail.deactivateCouponDialog.body', { count: codesQuery.data?.content.length ?? 0 })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirm(null)}>{t('common:action.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              pauseMutation.mutate(coupon.id, {
                onSuccess: () => {
                  success(t('couponDetail.couponDeactivated'));
                  setDeactivateConfirm(null);
                },
                onError: (e) => notifyError(e.message),
              })
            }
          >
            {t('couponDetail.deactivateCoupon')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deactivateConfirm === 'selection'} onClose={() => setDeactivateConfirm(null)}>
        <DialogTitle>{t('couponDetail.deactivateSelectedDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('couponDetail.deactivateSelectedDialog.body', { count: selected.size })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirm(null)}>{t('common:action.cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => void handleBulkDeactivate()}>
            {t('couponDetail.deactivateSelected')}
          </Button>
        </DialogActions>
      </Dialog>

      {!canManage ? <Alert severity="info" sx={{ mt: 2 }}>{t('builder.readOnlyAlert')}</Alert> : null}

      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={clear} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
